import React from 'react';
import { useLang } from '../LangContext';

const PALETTES = [
  {
    value: "ci",
    label: "Orange</>Green",
    flag: "",
    light: {
      accent: "#f77f00",
      accent2: "#0b8a4a",
      bg: "#fbf8f3",
      fg: "#0c1410",
      fgMuted: "#4a544e",
      line: "rgba(12,20,16,0.12)",
    },
    dark: {
      accent: "#ff9933",
      accent2: "#1fbf6c",
      bg: "#07100b",
      fg: "#fbf8f3",
      fgMuted: "#8a948e",
      line: "rgba(251,248,243,0.14)",
    },
  },
  {
    value: "ca",
    label: "Red</>Cyan",
    flag: "",
    light: {
      accent: "#d91f2d",
      bg: "#f6f4ef",
      fg: "#0a0a0a",
      fgMuted: "#555550",
      line: "rgba(10,10,10,0.12)",
    },
    dark: {
      accent: "#ff3344",
      bg: "#0a0a0a",
      fg: "#f6f4ef",
      fgMuted: "#8a8780",
      line: "rgba(246,244,239,0.14)",
    },
  },
];

function MockPreview({ colors }) {
  const { accent, accent2, bg, fg, fgMuted, line } = colors;
  return (
    <div className="tp-mock" style={{ background: bg }}>
      <div className="tp-mock__nav" style={{ borderBottom: `1px solid ${line}` }}>
        <span className="tp-mock__dot" style={{ background: accent }} />
        <span className="tp-mock__brand" style={{ color: fg }}>TL — 2.0</span>
        <span className="tp-mock__live" style={{ color: accent }}>● work</span>
      </div>
      <div className="tp-mock__body">
        <div className="tp-mock__tag" style={{ color: accent }}>{'// dev'}</div>
        <div className="tp-mock__name" style={{ color: fg }}>
          TRAORE<br />LAMINE.
        </div>
        <div className="tp-mock__sub" style={{ color: fgMuted }}>Fullstack · 5Y · Abidjan</div>
        <div className="tp-mock__chips">
          <span style={{ background: accent }} />
          {accent2 && <span style={{ background: accent2 }} />}
          <span style={{ background: fgMuted, opacity: 0.3 }} />
        </div>
      </div>
      <div className="tp-mock__foot" style={{ borderTop: `1px solid ${line}` }}>
        <span style={{ color: fgMuted }}>React · Node · Postgres</span>
      </div>
    </div>
  );
}

export default function ThemePanel({ open, onClose, palette, dark, setTweak }) {
  const { t } = useLang();
  const th = t.theme;

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <div className={`tpanel${open ? ' tpanel--open' : ''}`} onMouseDown={onClose}>
      <div className="tpanel__box" onMouseDown={(e) => e.stopPropagation()}>

        <div className="tpanel__head">
          <span className="tpanel__head-label">{th.heading}</span>
          <button className="tpanel__esc" onClick={onClose}>ESC</button>
        </div>

        {/* PALETTE */}
        <div className="tpanel__section">
          <div className="tpanel__section-label">
            <span>{th.paletteLabel}</span>
            <span className="tpanel__section-hint">{th.paletteHint}</span>
          </div>
          <div className="tpanel__grid">
            {PALETTES.map((p, i) => {
              const colors = dark ? p.dark : p.light;
              const active = palette === p.value;
              return (
                <button
                  key={p.value}
                  className={`tpanel__card${active ? ' tpanel__card--active' : ''}`}
                  style={{ '--delay': `${i * 80}ms`, '--c': colors.accent }}
                  onClick={() => setTweak('palette', p.value)}
                >
                  <div className="tpanel__mock-wrap">
                    <MockPreview colors={colors} />
                    {active && (
                      <div className="tpanel__card-badge">
                        <span>{th.active}</span>
                      </div>
                    )}
                  </div>
                  <div className="tpanel__card-foot">
                    <div className="tpanel__card-info">
                      <span className="tpanel__card-flag">{p.flag}</span>
                      <span className="tpanel__card-name">{p.label}</span>
                    </div>
                    <span className="tpanel__card-hex" style={{ color: colors.accent }}>
                      {colors.accent}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* MODE */}
        <div className="tpanel__section">
          <div className="tpanel__section-label">
            <span>{th.modeLabel}</span>
          </div>
          <div className="tpanel__grid tpanel__grid--mode">
            {[
              { value: false, icon: '☀', label: 'Light', bg: '#f6f4ef', fg: '#0a0a0a', line: 'rgba(10,10,10,0.12)' },
              { value: true,  icon: '●', label: 'Dark',  bg: '#0a0a0a', fg: '#f6f4ef', line: 'rgba(246,244,239,0.14)' },
            ].map((m, i) => {
              const active = dark === m.value;
              const currentAccent = PALETTES.find((p) => p.value === palette)?.[m.value ? 'dark' : 'light']?.accent ?? '#d91f2d';
              return (
                <button
                  key={m.label}
                  className={`tpanel__card tpanel__card--mode${active ? ' tpanel__card--active' : ''}`}
                  style={{ '--delay': `${(i + 2) * 80}ms`, '--c': currentAccent }}
                  onClick={() => setTweak('dark', m.value)}
                >
                  <div className="tpanel__mode-mock" style={{ background: m.bg }}>
                    <div className="tpanel__mode-bar" style={{ background: m.fg, opacity: 0.7, width: '45%' }} />
                    <div className="tpanel__mode-bar" style={{ background: m.fg, opacity: 0.4, width: '65%' }} />
                    <div className="tpanel__mode-bar" style={{ background: currentAccent, width: '30%' }} />
                    <div className="tpanel__mode-bar" style={{ background: m.fg, opacity: 0.2, width: '55%' }} />
                  </div>
                  <div className="tpanel__card-foot">
                    <span className="tpanel__card-name">
                      {m.icon} {m.label}
                    </span>
                    {active && <span className="tpanel__card-active">{th.active}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}
